<script setup lang="ts">
const { t, locale } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const tenant = useTenant()
const auth = useAuth()
const route = useRoute()
const mobileMenuOpen = ref(false)

if (!auth.user.value) {
  await auth.init()
}

const activeTenant = computed(() => tenant.tenant.value)
const showTenantFooter = computed(() => Boolean(activeTenant.value))
const isTenantDomain = computed(() => tenant.tenantMode.value !== 'platform')
const user = computed(() => auth.user.value)
const isAuthenticated = computed(() => Boolean(user.value))
const memberInitials = computed(() => {
  const name = user.value?.name?.trim() || ''
  if (!name) return 'ME'
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(item => item.charAt(0).toUpperCase())
    .join('')
})

const publicNavItems = [
  { key: 'nav.agencies', to: '/', hash: '#agencies' },
  { key: 'nav.contact', to: '/legal/contact', hash: '' },
]

const navItems = computed(() => isTenantDomain.value ? [] : publicNavItems)
const dashboardPath = computed(() => auth.getPostLoginRedirect())

const handleLogout = async () => {
  await auth.logout()
  await navigateTo(localePath('/'))
}

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
})

const scrolled = ref(false)

onMounted(() => {
  const onScroll = () => { scrolled.value = window.scrollY > 10 }
  window.addEventListener('scroll', onScroll, { passive: true })
  onUnmounted(() => window.removeEventListener('scroll', onScroll))
})
</script>

<template>
  <UApp>
    <header
      class="sticky top-0 z-50 transition-all duration-300"
      :class="scrolled
        ? 'border-b border-default/60 bg-default/85 backdrop-blur-md shadow-sm'
        : 'border-b border-transparent bg-default/50 backdrop-blur'"
    >
      <nav aria-label="Main navigation" class="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2.5 lg:px-8">
        <!-- Logo + nav links (grouped to balance the right side) -->
        <div class="flex items-center gap-7">
          <NuxtLink
            to="/"
            class="flex shrink-0 items-center gap-2.5"
          >
            <img
              src="/images/logo.png"
              alt="agency-saas logo"
              class="size-8"
            >
            <span class="text-base font-semibold tracking-tight">{{ activeTenant?.name || 'agency-saas' }}</span>
          </NuxtLink>

          <!-- Desktop nav -->
          <div class="hidden items-center gap-1 lg:flex">
            <NuxtLink
              v-for="item in navItems"
              :key="item.to + item.hash"
              :to="item.hash ? { path: localePath(item.to), hash: item.hash } : localePath(item.to)"
              class="rounded-md px-3 py-1.5 text-sm font-medium ui-text-subtle transition hover:bg-default/80 hover:text-[var(--ui-text-strong)]"
            >
              {{ t(item.key) }}
            </NuxtLink>
          </div>
        </div>

        <!-- Right side actions -->
        <div class="hidden items-center gap-1.5 lg:flex">
          <!-- Language switcher -->
          <UButton
            color="neutral"
            variant="ghost"
            size="sm"
            icon="i-lucide-globe"
            :to="switchLocalePath(locale === 'mn' ? 'en' : 'mn')"
            class="uppercase"
          >
            {{ locale }}
          </UButton>
          <ClientOnly>
            <UColorModeButton />
          </ClientOnly>

          <span class="mx-1 h-5 w-px bg-default/80" />

          <template v-if="!isAuthenticated">
            <UButton
              :to="localePath('/login')"
              color="neutral"
              variant="ghost"
              size="sm"
            >
              {{ t('auth.login') }}
            </UButton>
            <UButton
              :to="localePath('/register')"
              color="primary"
              size="sm"
              class="font-semibold"
            >
              {{ t('auth.signup') }}
            </UButton>
          </template>

          <template v-else>
            <!-- Avatar → dropdown menu -->
            <UPopover :content="{ align: 'end', side: 'bottom', sideOffset: 8 }">
              <button class="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <UAvatar
                  :src="user?.avatar || undefined"
                  :text="memberInitials"
                  size="sm"
                />
              </button>
              <template #content>
                <div class="w-48 p-1.5">
                  <div class="px-2 py-2 mb-1 border-b ui-border-base">
                    <p class="text-sm font-semibold truncate">{{ user?.name }}</p>
                    <p class="text-xs truncate opacity-60">{{ user?.email }}</p>
                  </div>
                  <NuxtLink
                    :to="dashboardPath"
                    class="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:ui-bg-base transition-colors"
                  >
                    <UIcon name="i-lucide-layout-dashboard" class="size-4 opacity-60" />
                    {{ t('app_shell.dashboard') }}
                  </NuxtLink>
                  <NuxtLink
                    :to="localePath('/me')"
                    class="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:ui-bg-base transition-colors"
                  >
                    <UIcon name="i-lucide-user" class="size-4 opacity-60" />
                    {{ t('app_shell.my_profile') }}
                  </NuxtLink>
                  <div class="my-1 border-t ui-border-base" />
                  <button
                    class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:ui-bg-base transition-colors"
                    @click="handleLogout"
                  >
                    <UIcon name="i-lucide-log-out" class="size-4 opacity-60" />
                    {{ t('auth.logout') }}
                  </button>
                </div>
              </template>
            </UPopover>
          </template>
        </div>

        <!-- Mobile menu toggle -->
        <UButton
          class="lg:hidden"
          color="neutral"
          variant="ghost"
          :icon="mobileMenuOpen ? 'i-lucide-x' : 'i-lucide-menu'"
          :aria-expanded="mobileMenuOpen"
          aria-label="Toggle navigation menu"
          @click="mobileMenuOpen = !mobileMenuOpen"
        />
      </nav>

      <!-- Mobile menu -->
      <div
        v-if="mobileMenuOpen"
        aria-label="Mobile navigation"
        class="border-t ui-border-base ui-bg-base px-4 py-4 lg:hidden"
      >
        <div class="space-y-1">
          <NuxtLink
            v-for="item in navItems"
            :key="item.to + item.hash"
            :to="item.hash ? { path: localePath(item.to), hash: item.hash } : localePath(item.to)"
            class="block rounded-md px-3 py-2 text-sm font-medium hover:ui-bg-base"
          >
            {{ t(item.key) }}
          </NuxtLink>

          <template v-if="isAuthenticated">
            <NuxtLink
              :to="dashboardPath"
              class="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:ui-bg-base"
            >
              <UAvatar
                :src="user?.avatar || undefined"
                :text="memberInitials"
                size="xs"
              />
              {{ user?.name }}
            </NuxtLink>
          </template>
        </div>

        <div class="mt-3 flex items-center gap-2">
          <UButton
            color="neutral"
            variant="ghost"
            size="sm"
            icon="i-lucide-orbit"
            :to="switchLocalePath(locale === 'mn' ? 'en' : 'mn')"
          >
            {{ locale }}
          </UButton>
          <ClientOnly>
            <UColorModeButton />
          </ClientOnly>
        </div>

        <div
          v-if="!isAuthenticated"
          class="mt-3 grid grid-cols-2 gap-2"
        >
          <UButton
            :to="localePath('/login')"
            color="neutral"
            variant="outline"
            class="justify-center"
          >
            {{ t('auth.login') }}
          </UButton>
          <UButton
            :to="localePath('/register')"
            color="primary"
            class="justify-center"
          >
            {{ t('auth.signup') }}
          </UButton>
        </div>

        <div
          v-else
          class="mt-3"
        >
          <UButton
            color="neutral"
            variant="outline"
            class="w-full justify-center"
            @click="handleLogout"
          >
            {{ t('auth.logout') }}
          </UButton>
        </div>
      </div>
    </header>

    <UMain>
      <slot />
    </UMain>

    <FooterTenantFooter
      v-if="showTenantFooter && activeTenant"
      :tenant="activeTenant"
    />
    <FooterMarketplaceFooter v-else />

    <ClientOnly>
      <ScrollToTop />
    </ClientOnly>

  </UApp>
</template>
